/**
 * VENDOR REGISTRY - Single Source of Truth for All Vendor Configuration
 * 
 * This file defines ALL vendor-specific configuration, behavior, and metadata.
 * When adding a new vendor, you only need to add an entry here and create the 
 * corresponding product/metrics databases.
 * 
 * @module vendorRegistry
 */

import aicLogo from '../AIC.png';
import dellLogo from '../DELL.png';
import supermicroLogo from '../SUPERMICRO.png';
import vduraAicLogo from '../logo.svg';
import vduraDellLogo from '../vduradell.png';
import vduraSmcLogo from '../vdurasmc.png';

import productDbAIC from '../constants/productDb-AIC.json';
import productDbDELL from '../constants/productDb-DELL.json';
import productDbSMC from '../constants/productDb-SMC.json';
import metricsDbAIC from '../constants/metricsDb-AIC.json';
import metricsDbDELL from '../constants/metricsDb-DELL.json';
import metricsDbSMC from '../constants/metricsDb-SMC.json';

// ============================================================================
// VENDOR CONFIGURATION SCHEMA
// ============================================================================

/**
 * @typedef {Object} VendorConfig
 * @property {string} id - Unique vendor identifier (uppercase)
 * @property {string} displayName - Human-readable vendor name
 * @property {boolean} isActive - Whether vendor is currently available
 * 
 * @property {Object} metadata - Vendor metadata
 * @property {string} metadata.cognitoGroup - AWS Cognito group name for access control
 * @property {string} metadata.hardwarePartner - Legal entity name for T&C
 * 
 * @property {Object} branding - Vendor branding assets
 * @property {any} branding.logo - Vendor logo asset
 * @property {any} branding.combinedLogo - VDURA + Vendor combined logo asset
 * @property {string} branding.logoClassName - CSS class for logo sizing
 * @property {Object} branding.theme - Tailwind CSS classes for theming
 * 
 * @property {Object} capabilities - What the vendor hardware supports
 * @property {number} capabilities.defaultJbodSize - Default JBOD bay count
 * @property {number[]} capabilities.availableJbodSizes - Supported JBOD bay counts
 * @property {number[]} capabilities.availableSpares - Supported spare drive counts
 * @property {boolean} capabilities.supportsHybrid - Supports hybrid storage
 * @property {boolean} capabilities.supportsAFA - Supports all-flash arrays
 * @property {boolean} capabilities.supportsDualCpuFlash - Offers the Dual CPU flash node (CONF-210)
 * @property {boolean} capabilities.supportsE2E - Supports end-to-end encryption
 * @property {Object} capabilities.rackHeights - Rack units per item: { director, afa, hybrid, jbod }.
 *   Required, no default; read through getRackHeights (CONF-230)
 * 
 * @property {Object} businessRules - Vendor-specific business logic
 * @property {boolean} businessRules.forceConsultationMode - Always enable consultation mode
 * @property {boolean} businessRules.softwareSubscriptionOnly - Quote/BOM/PDF shows software subscription only; hardware is supplied by the vendor partner and not itemised
 * @property {boolean} businessRules.requiresSpecialPricing - Needs special pricing access
 * @property {number} businessRules.minSubscriptionMonths - Minimum subscription term
 * @property {number} businessRules.defaultAfaCompressionRatio - Default afa compression ratio
 * @property {number} businessRules.defaultHybridCompressionRatio - Default hybrid compression ratio
 * 
 * @property {Object} databases - Product and metrics databases
 * @property {Object} databases.products - Product database JSON
 * @property {Object} databases.metrics - Metrics database JSON
 * 
 * @property {Object} termsAndConditions - Legal terms and conditions
 * @property {string} termsAndConditions.title - T&C document title
 * @property {string} termsAndConditions.content - Full T&C text (markdown supported)
 * @property {string} termsAndConditions.orderText - Ordering instructions
 */

// ============================================================================
// VENDOR DEFINITIONS
// ============================================================================

export const VENDORS = {
  AIC: {
    id: 'AIC',
    displayName: 'AIC',
    isActive: true,
    
    metadata: {
      // A real Cognito group, like dellHardware/smcHardware. AIC used to be a
      // 'defaultAccess' sentinel that unlocked for everyone; since 2026-09-22 a
      // user with no vendor group lands on the Configurator Home page instead.
      cognitoGroup: 'aicHardware',
      hardwarePartner: 'ADVANCED INFORMATION CONCEPTS'
    },
    
    branding: {
      logo: aicLogo,
      combinedLogo: vduraAicLogo,
      logoClassName: 'w-80',
      logoStyle: {
        filter: 'brightness(0) saturate(100%) invert(56%) sepia(89%) saturate(2476%) hue-rotate(360deg) brightness(102%) contrast(101%)'
      },
      
      theme: {
        // Header & Cards
        cardHeader: 'bg-vduraColor',
        cardTitle: 'text-xl font-bold text-gray-800',

        // Buttons
        button: 'bg-orange-500 hover:bg-orange-600 text-white',
        saveButton: 'bg-blue-500 hover:bg-blue-600 text-white',
        loadButton: 'bg-green-500 hover:bg-green-600 text-white',
        summaryButton: 'bg-orange-500 hover:bg-orange-600 text-white',
        quoteButton: 'bg-red-500 hover:bg-red-600 text-white',

        // Accents
        accentColor: 'bg-orange-500',
        accentHover: 'hover:bg-orange-400',

        // Form Elements
        selectTrigger: 'hover:bg-gray-100/50',
        selectItem: 'text-white hover:bg-gray-100/50',
        inputField: 'border border-gray-300 hover:bg-gray-100/50 bg-black text-white',

        // Toggle
        toggleActive: 'bg-orange-500',
        toggleInactive: 'bg-gray-700',
        toggleDot: 'bg-white',
        toggleLabelClassName: 'text-gray-800',

        // Table
        tableHeader: 'bg-vduraColor text-black font-bold',
        tableRow: 'hover:bg-gray-100/40'
      }
    },
    
    capabilities: {
      serviceOptions: [
        { key: 'standard',         label: 'Next Business Day' },
        { key: 'noReturnMedia',    label: 'No Return Media' },
        { key: 'noReturnHardware', label: 'No Return Hardware' }
      ],
      defaultJbodSize: 78,
      availableJbodSizes: [30, 78, 108],
      jbodSizeLabels: {
        30:  "4U78 | x30 HDDs",
        78:  "4U78 | x78 HDDs",
        108: "4U108 | x108 HDDs"
      },
      availableSpares: [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26],
      supportsHybrid: true,
      supportsAFA: true,
      // CONF-94: MLEC (per-VPOD raidz1 beneath the network D+P stripe) ships on
      // AIC. mlecJbodSizes lists the JBOD sizes it may be enabled on. The 4U108 is
      // 108 / 12 VPODs = exactly 9 HDDs each (11.1% to local parity).
      // CONF-222: the 78-drive 4U78 is added on request (Erik), reversing CONF-94's
      // original 4U108-only call. 78 / 12 = 6.5, realised as 6- and 7-drive VPODs;
      // MLEC takes one drive from each, so 12 of 78 go to parity (15.4%), the same
      // shape as the Supermicro 4U90. The 30-drive SKU (also a 4U78 chassis) stays
      // out: it would lose 40%. Enforced by mlecEligible — see domain/rules/mlecRules.
      // Go-ahead for 4U78 MLEC confirmed 2026-10-14 (CONF-220).
      supportsMLEC: true,
      mlecJbodSizes: [78, 108],
      // CONF-210: the Dual CPU flash node (VCH-5500-F1N) ships on AIC.
      // Gated further by the kill switch in dualCpuFlashEligible — see
      // domain/rules/dualCpuRules.
      supportsDualCpuFlash: true,
      supportsE2E: true,
      // Rack units per item, read by the Rack Units metric and the rack picture (CONF-230)
      rackHeights: { director: 1, afa: 1, hybrid: 1, jbod: 4 },
      // Director/Velo Configuration Options
      // Director count is entered as a number, not picked from a list: the floor
      // is a rule, the ceiling is whatever the customer buys. (CONF-181)
      directorCountMin: 3,
      expansionDirectorCountMin: 0, // Expansion allows 0
      veloSsdCapacities: [1.92, 3.84, 7.68],
      // Storage Configuration Options
      hddCapacities: [16, 24, 30, 32, 36, 40],
      hybridSsdCapacities: [3.84, 7.68, 15.36, 30.72],
      productMappings: {
        afaSsd: [
            { capacity: 3.84, partNumber: "VCH-NVME-3.8", displayName: "3.84TB NVMe" },
            { capacity: 7.68, partNumber: "VCH-NVME-7.6", displayName: "7.68TB NVMe" },
            { capacity: 15.36, partNumber: "VCH-NVME-15.3", displayName: "15.36TB NVMe" },
            { capacity: 30.72, partNumber: "VCH-NVME-30.7", displayName: "30.72TB NVMe" },
            // { capacity: 61.44, partNumber: "VCH-NVME-61.4", displayName: "61.44TB NVMe" },  // CONF-101: Temporarily removed - will re-add when product vendors have more stock
            { capacity: 30.72, partNumber: "VCH-NVME-30.7-QLC", displayName: "30.72TB QLC NVMe", mediaType: "qlc" },
            { capacity: 61.44, partNumber: "VCH-NVME-61.4-QLC", displayName: "61.44TB QLC NVMe", mediaType: "qlc" },
            { capacity: 122.88, partNumber: "VCH-NVME-122.8-QLC", displayName: "122.88TB QLC NVMe", mediaType: "qlc" }
        ],
        hybridSsd: [
            { capacity: 3.84, partNumber: "VCH-NVME-3.8", displayName: "3.84TB NVMe" },
            { capacity: 7.68, partNumber: "VCH-NVME-7.6", displayName: "7.68TB NVMe" },
            { capacity: 15.36, partNumber: "VCH-NVME-15.3", displayName: "15.36TB NVMe" },
            { capacity: 30.72, partNumber: "VCH-NVME-30.7", displayName: "30.72TB NVMe" }
        ],
        hybridHdd: {
            30: [
                { capacity: 16, partNumber: "VCH-5000-J78-480", displayName: "16TB HDD (480TB total)" },
                { capacity: 24, partNumber: "VCH-5000-J78-720", displayName: "24TB HDD (720TB total)" }
            ],
            78: [
                // { capacity: 0, partNumber: "VCH-5000-J78-0", displayName: "All Flash (No HDDs)" },     // MORK TODO : Do we still offer this?
                { capacity: 16, partNumber: "VCH-5000-J78-1248", displayName: "16TB HDD (1.25PB total)" },
                { capacity: 24, partNumber: "VCH-5000-J78-1872", displayName: "24TB HDD (1.87PB total)" },
                { capacity: 26, partNumber: "VCH-5000-J78-2028", displayName: "26TB HDD (2.02PB total)" },
                { capacity: 28, partNumber: "VCH-5000-J78-2184", displayName: "28TB HDD (2.19PB total)" },
                { capacity: 30, partNumber: "VCH-5000-J78-2340", displayName: "30TB HDD (2.34PB total)", enabled: false },                       // CONF-132: Temporarily hidden
                { capacity: 32, partNumber: "VCH-5000-J78-2496", displayName: "32TB HDD (2.5PB total)", availableFrom: "2026-Q3", enabled: false },   // CONF-132: Temporarily hidden
                { capacity: 36, partNumber: "VCH-5000-J78-2808", displayName: "36TB SMR HDD (2.81PB total)", availableFrom: "2026-Q3", enabled: false }, // CONF-132: Temporarily hidden
                { capacity: 40, partNumber: "VCH-5000-J78-3120", displayName: "40TB SMR HDD (3.12PB total)", availableFrom: "2027-Q1", enabled: false }  // CONF-132: Temporarily hidden
            ],
            108: [
                // { "capacity": 0, "partNumber": "VCH-5000-J108-0", "displayName": "All Flash (No HDDs)" },     // MORK TODO : Do we still offer this?
                { capacity: 16, partNumber: "VCH-5000-J108-1728", displayName: "16TB HDD (1.73PB total)" },
                { capacity: 24, partNumber: "VCH-5000-J108-2592", displayName: "24TB HDD (2.59PB total)" },
                { capacity: 26, partNumber: "VCH-5000-J108-2808", displayName: "26TB HDD (2.81PB total)" },
                { capacity: 28, partNumber: "VCH-5000-J108-3024", displayName: "28TB HDD (3.02PB total)" },
                { capacity: 30, partNumber: "VCH-5000-J108-3240", displayName: "30TB HDD (3.24PB total)", enabled: false },                       // CONF-132: Temporarily hidden
                { capacity: 32, partNumber: "VCH-5000-J108-3456", displayName: "32TB HDD (3.46PB total)", availableFrom: "2026-Q3", enabled: false },  // CONF-132: Temporarily hidden
                { capacity: 36, partNumber: "VCH-5000-J108-3888", displayName: "36TB SMR HDD (3.89PB total)", availableFrom: "2026-Q3", enabled: false }, // CONF-132: Temporarily hidden
                { capacity: 40, partNumber: "VCH-5000-J108-4320", displayName: "40TB SMR HDD (4.32PB total)", availableFrom: "2027-Q1", enabled: false }  // CONF-132: Temporarily hidden
            ]
        }
      },
      // afaSsdCapacities: [3.84, 7.68, 15.36, 30.72. 61.44, 61.44QLC, 128.88QLC],
      // Data processing options
      compressionRatios: [1.0, 1.25, 1.5, 1.75, 2.0, 3.0, 4.0, 5.0],

      // Network Options
      // CONF-93. Four options, mapping 1:1 onto the four CCI bundle families
      // (see cciSkuMapping.json). Ports-per-NIC come from the CONF-93
      // acceptance criteria; directorNicCards follows the CCI director
      // descriptions — single-port options are 2x SP and take OCP+PCIe.
      // Throughput ceilings deliberately live in metricsDb, not here: this is
      // catalog data, that is performance data.
      networkOptions: [
        // The next three options are ONE physical card, the Broadcom 200/100GbE
        // dual-port QSFP112 pair, in three link modes (Petros/Chris 2026-09-16,
        // Ken Haugen on CONF-93 2026-07-22). At native 400Gb the card aggregates
        // 2x200Gb and one port is disabled, so 400GbE carries portsPerNic 1 and
        // needs OCP+PCIe to reach two usable ports on a director; 200GbE and
        // 100GbE run the card dual-port off a single OCP card. They differ only
        // in link speed and therefore in cardCeiling, not in hardware.
        {
          id: '400GbE',
          label: '400GbE',
          vendor: 'Broadcom',
          cciSlot: 2,
          portsPerNic: 1,
          directorNicCards: 'OCP+PCIe',
          partNumbers: { ocp: 'VCH-5000-200GBE-2P-OCP', pcie: 'VCH-5000-200GBE-2P-PCIe' },
        },
        {
          id: '200GbE',
          label: '200GbE',
          vendor: 'Broadcom',
          cciSlot: 1,
          // The CCI sheet has no native 200GbE bundle — its four variants stop at
          // slot 1 "DP 100GbE" and slot 2 "2x SP 400GbE". 200GbE is the same card
          // and the same node build as slot 1, so it books the slot-1 bundle and
          // is BOM-identical to 100GbE. User decision 2026-09-17. FLAGGED: the
          // emitted CCI description still reads "DP 100GbE"; Chris/Petros to
          // confirm or issue a real 200GbE row (Ken's open question 2, CONF-93).
          cciVariant: '100GbE',
          portsPerNic: 2,
          directorNicCards: 'OCP',
          partNumbers: { ocp: 'VCH-5000-200GBE-2P-OCP', pcie: 'VCH-5000-200GBE-2P-PCIe' },
        },
        {
          id: 'NDR200/200GbE',
          label: 'NDR200/200GbE',
          vendor: 'NVIDIA',
          cciSlot: 3,
          portsPerNic: 2,
          directorNicCards: 'OCP',
          partNumbers: { ocp: 'VCH-5000-NDR200-2P-OCP', pcie: 'VCH-5000-NDR200-2P-PCIe' },
        },
        {
          id: 'NDR/400GbE',
          label: 'NDR/400GbE',
          vendor: 'NVIDIA',
          cciSlot: 4,
          portsPerNic: 1,
          directorNicCards: 'OCP+PCIe',
          partNumbers: { ocp: 'VCH-NDR400-1P-OCP', pcie: 'VCH-NDR400-1P-PCIE' },
        },
        {
          id: '100GbE',
          label: '100GbE',
          vendor: 'Broadcom',
          cciSlot: 1,
          portsPerNic: 2,
          directorNicCards: 'OCP',
          partNumbers: { ocp: 'VCH-5000-200GBE-2P-OCP', pcie: 'VCH-5000-200GBE-2P-PCIe' },
        },
      ],
      networkProtocols: ['TCP', 'RDMA'],

      /**
       * SP3: the admin catalog editor's grouping. Ordered — FIRST MATCH WINS.
       * Dev-owned data on purpose: a category cannot live on `CatalogComponent`,
       * whose rows are immutable and time-versioned, without making the category
       * itself time-varying (spec §2.2).
       *
       * `node: true` defers to `isNodeSku` in seedBuilder rather than restating
       * the rule — network cards and nodes share the `CMP-BASE-VCH-5000-` prefix
       * and only that regex separates them.
       *
       * Labels and membership are provisional (user decision 2026-08-10); the
       * MECHANISM is what is settled. Anything unmatched shows as Uncategorised,
       * which is always rendered.
       */
      catalogCategories: [
        // SP3a: ORDERING IS LOAD-BEARING. `notDecomposed` must precede
        // `residual` — it is the narrower rule — and BOTH must precede every
        // part category below. `nodes` carries `node: true`, and
        // isNodeSku('VCH-5000-S1N') is true, so if a part category ran first
        // CMP-BASE-VCH-5000-S1N would be claimed by Nodes and never reach a
        // residual bucket. Reordering these entries fails categories.test.js
        // by design.
        { id: 'notDecomposed', label: 'Not decomposed — price is a lump', kinds: ['baseRemainder'], notDecomposed: true },
        { id: 'residual',      label: 'Residual remainders',              kinds: ['baseRemainder'] },
        { id: 'jbod',    label: 'JBOD enclosures', kinds: ['chassis'] },
        { id: 'hddFru',  label: 'HDD FRUs',        prefixes: ['VCH-FRU-'] },
        { id: 'ssd',     label: 'SSD / NVMe',      prefixes: ['CMP-SSD-'] },
        { id: 'memory',  label: 'Memory',          prefixes: ['CMP-DRAM-'] },
        // Unreachable BY CONSTRUCTION, not merely empty today: network cards
        // and node residuals are only ever `baseRemainder`, and `residual`/
        // `notDecomposed` above claim every `baseRemainder` row first — so
        // `network`'s productPrefixes and `nodes`' `node: true` below can
        // never fire.
        { id: 'network', label: 'Network fabric',  productPrefixes: ['VCH-5000-200GBE', 'VCH-5000-NDR200'] },
        { id: 'nodes',   label: 'Nodes',           prefixes: ['CMP-LABOR-'], node: true },
      ],

      /**
       * SP3a: which catalog categories the GRID and IMPACT editors may EDIT.
       * Everything else renders read-only there — visible, but not interactive.
       *
       * This narrows exactly two render branches — GridView.jsx:238 and
       * ImpactView.jsx:233 — it is not an enforced permission boundary. The
       * `/admin/catalog/import` path (ImportPage -> matchDraft -> planWrites ->
       * commit) is gated by the same `catalogAuthor` Cognito group but applies
       * no category check of its own, so the same user who cannot click a
       * chassis cell in the grid can upload a CSV that writes one. Closing
       * that gap is a separate ticket, not this one.
       *
       * Editing is authorised for drive costs and memory only; SP3 shipped an
       * editor that opened on any cost cell, which was wider than the business
       * had agreed. Widening it again must be a reviewable one-line data change
       * rather than a code change.
       *
       * Chassis, residual remainders and labor are deliberately absent.
       * `CMP-DISC-HW` never appears here regardless: it sets the list price the
       * whole book derives from, so 1% moves every hardware price.
       */
      catalogEditableCategories: ['hddFru', 'ssd', 'memory'],

      /**
       * SP4: PRODUCT families, for the product catalog view. Ordered, and
       * first-match-wins — the same rule `catalogCategories` follows.
       *
       * Distinct from `catalogCategories`, which classifies COMPONENTS by
       * `kind`. These classify PRODUCTS by SKU shape, one level up. A SKU
       * matching nothing renders as `unclassified` rather than being absorbed
       * by a neighbouring family, so a new naming pattern is visible.
       *
       * `nodes` first: a node SKU is matched by predicate, and the JBOD prefix
       * `VCH-5000-J` would otherwise be free to claim anything that shape.
       */
      productFamilies: [
        { id: 'nodes',   label: 'Nodes',            node: true },
        { id: 'jbods',   label: 'JBOD enclosures',  prefixes: ['VCH-5000-J', 'VCH-5050-J'] },
        { id: 'drives',  label: 'Drives',           prefixes: ['VCH-NVME-'] },
        { id: 'network', label: 'Network fabric',   contains: ['GBE', 'NDR'] },
      ],

    },

    businessRules: {
      forceConsultationMode: false,
      softwareSubscriptionOnly: false,
      requiresSpecialPricing: false,
      minSubscriptionMonths: 36,
      defaultAfaCompressionRatio: 1.0,
      defaultHybridCompressionRatio: 1.25,
      registrationMultiplier: 1.4
    },

    databases: {
      products: productDbAIC,
      metrics: metricsDbAIC
    },
    
    termsAndConditions: {
      title: 'Terms and Conditions',
      content: `**VDURA COMPLETE SOLUTION TERMS AND CONDITIONS**

**Solution Overview**

**The solution quoted consists of two components: (1) Software-as-a-Service (SaaS) provided directly by VDURA, Inc., and (2) VDURA certified commodity storage hardware provided by VDURA's contract manufacturer. Customer must issue separate purchase orders for each component: one purchase order to VDURA for SaaS services, and one purchase order to the designated contract manufacturer for hardware.**

**Purchase orders may be emailed to: orders@vdura.com or faxed to: Sales Operations (001) 510-952-3094**

**Hardware Terms** 

**Hardware will be manufactured and shipped by VDURA's third-party contract manufacturer. Unless otherwise specified in the quote, all hardware shipments are FOB California. VDURA endeavors to hold the total solution price stated in the quote for the timeframe specified therein, regardless of commodity pricing fluctuations. In the case of extraordinary commodity price changes from the original quote date, VDURA reserves the right to re-price the quote.**

**VDURA SaaS Terms and Conditions**

1.\t**Contract Formation** VDURA, Inc. ("VDURA") will provide software-as-a-service ("SaaS") offerings and related support (collectively, the "Services") to the Customer (defined as the end user of the Services who is purchasing or subscribing to VDURA's Services and entering into this Agreement) subject exclusively to these Terms and Conditions ("Agreement"). By placing an order for, subscribing to, or using VDURA's Services, Customer agrees to be bound by this Agreement. The Agreement will become effective upon VDURA's acceptance of Customer's order, which may occur through provision of the Services or written confirmation. Any modifications must be explicitly agreed in writing by both parties. A third-party system integrator will deliver and integrate the specified hardware (“Solution Hardware,” defined in the Customer's executed quote) with VDURA software prior to delivery. VDURA provides warranty, maintenance, and support services for purchased products according to the Customer's selected service offerings. 

Support includes (i) accepting initial service requests via my.VDURA.com, telephone, email, or electronic transmission regarding Solution Hardware; (ii) commercially reasonable efforts to diagnose and resolve issues by trained personnel; (iii) providing Return Material Authorization (RMA) numbers for warranty repairs; (iv) responding to technical, billing, and general inquiries about Solution Hardware; (v) replacing defective Solution Hardware under warranty; and (vi) distributing Updates. "Update(s)" means subsequent releases of VDURA software products or documentation generally available to supported Customers without additional license fees, excluding separately sold or licensed programs.

2.\t**Pricing and Payment** All software and support (SaaS) pricing is subscription-based, stated in VDURA's official quote or invoice, exclusive of taxes, duties, tariffs, and additional fees (such as handling, processing, or third-party charges), which remain Customer's responsibility. All payment terms for VDURA invoices are strictly net thirty (30) days from invoice date unless otherwise specified. Late payments incur interest at 1.5% per month or the maximum permitted by law. Customers will be billed separately by the hardware provider for the Solution Hardware and by VDURA for software, support, and installation services. Customers must issue two separate purchase orders (POs) that will be billed against separately for the solution: one PO to be issued to and invoiced by the hardware provider for the hardware, and another PO to be issued to and invoiced by VDURA for software, support, and installation services.

3.\t**Service Delivery** VDURA provides the Services via secure online access. Customer gains immediate access to the my.VDURA.com service portal. Physical products or hardware replacement (if applicable) are provided according to separately specified terms, with delivery dates being estimates only.

4.\t**Software License** SaaS Licensing and Usage Services are licensed, not sold, strictly according to VDURA's End-User License Agreement (EULA). The Customer is granted a non-exclusive, non-transferable license for the duration of the subscription period. Unauthorized sharing, copying, or modification of the Services is prohibited.

5.\t**Software Updates and Maintenance** VDURA continuously updates the Services, providing automatic and periodic enhancements, bug fixes, and improvements without additional charges during the subscription period. Customer must accept and deploy updates promptly to maintain optimal functionality and support. "Update(s)" refers to subsequent releases of VDURA software products or documentation generally available at no additional fee, provided the Customer is under a current support subscription.

6.\t**Support Services**
\t\t**A. Support Commencement** Support services commence on the equipment ship date plus thirty (30) days, rounded up to the nearest first (1st) or sixteenth (16th) of the month, unless otherwise agreed to in writing by both parties.
\t\t**B. Technical Support** Unless governed by a separate Statement of Work (SOW), VDURA provides technical support using commercially reasonable efforts to identify defective source code, provide corrections, workarounds, and/or patches to correct program errors. VDURA offers support via my.VDURA.com, email, and 24/7 telephone. VDURA logs each service request (trouble ticket), provides technical support request identification numbers, and communicates resolution plans. VDURA uses commercially reasonable efforts to resolve significant program errors by offering workarounds, patches, or corrections in subsequent Updates. Customer acknowledges that technical support may require remote access or a duplicate of Customer's platform or application.
\t\t**C. Email Assistance** VDURA provides reasonable email support for technical inquiries from Customer's designated technical liaison regarding the use and operation of VDURA Products, responding promptly to inquiries.
\t\t**D. Support Exclusions** VDURA is not required to provide support for: (a) unsupported software versions; (b) unpaid fees; (c) breach of this Agreement; (d) unauthorized or improper use; (e) use on unsupported platforms; (f) damage or errors caused by misuse, neglect, unauthorized modifications, defective media, third-party acts, or refusal to implement Updates. Additionally, VDURA is not obligated to address errors from modifications not performed by VDURA or media duplication errors.
\t\t**E. Customer Responsibilities** Customer must designate qualified technical staff and at least one alternate as primary liaisons for technical support communications. Customer may substitute technical contacts with equal or better qualifications upon prior notice. Customer agrees to pay additional fees at VDURA's standard rates for support requests that fall outside VDURA's obligations or are caused by hardware or software not supplied by VDURA. VDURA retains sole discretion in accepting or rejecting enhancement requests.

7.\t**Warranty and Liability** The Services are provided "as is" without implied warranties of merchantability or fitness for a particular purpose. VDURA explicitly disclaims all warranties except those expressly stated herein. VDURA's liability, to the maximum extent permitted by applicable law, shall not exceed fees paid by Customer for Services during the preceding twelve (12) months. VDURA disclaims liability for indirect, incidental, consequential, or special damages.

8.\t**Claims and Refunds** Customer claims relating to Service performance must be reported within thirty (30) calendar days of occurrence. VDURA will investigate and, at its discretion, may offer remedies such as service credits or adjustments. Physical product returns require an authorized Return Material Authorization (RMA) number and adherence to VDURA's RMA policies.

9.\t**Intellectual Property (IP)** Each party retains all rights, titles, and interest in and to its own intellectual property rights. Nothing in this Agreement transfers any ownership of intellectual property rights between VDURA and the Customer. Customer acknowledges that all VDURA-provided software, documentation, and related materials constitute VDURA's intellectual property, and Customer shall not acquire any ownership or proprietary interest therein.

10.\t**Force Majeure** VDURA is not liable for any failure or delay in performing its obligations due to circumstances beyond its control, including but not limited to acts of nature, cyber-attacks, third-party service disruptions, pandemics, or governmental actions.

11.\t**Compliance and Indemnification** Customer must comply with all applicable laws regarding data protection (GDPR, CCPA), cybersecurity, export controls, anti-corruption, and use of the Services. Customer agrees to indemnify and hold VDURA harmless from claims arising from unauthorized use, non-compliance, or misuse of the Services.

12.\t**Data Protection and Privacy** VDURA maintains industry-standard measures to ensure the security and confidentiality of Customer data. VDURA adheres to recognized data protection standards, including but not limited to GDPR and SOC 2 compliance. Customer agrees that VDURA may access Customer data solely for the purposes of providing and improving Services and complying with applicable law.

13.\t**General Provisions** This Agreement is governed by the laws of the State of Delaware, United States, without reference to conflict of laws principles. Any disputes arising under this Agreement will be resolved exclusively in Delaware's federal or state courts. Unenforceable provisions shall not affect the Agreement's remainder. Electronic signatures and electronic acceptance of this Agreement constitute valid execution.

© 2025 VDURA, Inc. All rights reserved.`,
      orderText: ''
    }
  },
  
  DELL: {
    id: 'DELL',
    displayName: 'Dell',
    isActive: true,
    
    metadata: {
      cognitoGroup: 'dellHardware',
      hardwarePartner: 'DELL TECHNOLOGIES'
    },
    
    branding: {
      logo: dellLogo,
      combinedLogo: vduraDellLogo,
      logoClassName: 'w-[550px]',
      logoStyle: undefined,
      
      theme: {
        // Header & Cards
        cardHeader: 'bg-blue-800',
        cardTitle: 'text-xl font-bold text-white',

        // Buttons
        button: 'bg-blue-500 hover:bg-blue-600 text-white',
        saveButton: 'bg-blue-500 hover:bg-blue-600 text-white',
        loadButton: 'bg-green-500 hover:bg-green-600 text-white',
        summaryButton: 'bg-blue-500 hover:bg-blue-600 text-white',
        quoteButton: 'bg-red-500 hover:bg-red-600 text-white',

        // Accents
        accentColor: 'bg-blue-500',
        accentHover: 'hover:bg-blue-400',

        // Form Elements
        selectTrigger: 'hover:bg-gray-100/50',
        selectItem: 'text-white hover:bg-gray-100/50',
        inputField: 'border border-gray-300 hover:bg-gray-100/50 bg-black text-white',

        // Toggle
        toggleActive: 'bg-blue-500',
        toggleInactive: 'bg-gray-700',
        toggleDot: 'bg-white',
        toggleLabelClassName: 'text-white',

        // Table
        tableHeader: 'bg-blue-800 text-white font-bold',
        tableRow: 'hover:bg-gray-100/40'
      }
    },
    
    capabilities: {
      serviceOptions: [
        { key: 'dellSoftwareOnly',  label: 'Software Only' },
        // Hidden from the dropdown (Software Only is the sole offering); kept so
        // saved quotes using these options still resolve part numbers.
        { key: 'dellNbdMedia',      label: 'Next Business Day Media Only', hidden: true },
        { key: 'dellNoReturnMedia', label: 'No Return Media Only', hidden: true }
      ],
      defaultJbodSize: 60,
      availableJbodSizes: [60],  // Dell supports 120-bay
      availableSpares: [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26],
      supportsHybrid: true,
      supportsAFA: true,
      // CONF-94: MLEC is not offered on Dell. 60 / 16 VPODs is 3.75 HDDs each, so
      // single parity would cost 25-33% of usable capacity, and Dell's durability
      // path still carries the hard-coded /12 defect (design doc section 8.2).
      // Enabling it is supportsMLEC: true plus an mlecJbodSizes list, plus tests.
      supportsMLEC: false,
      // CONF-210: no Dual CPU flash node SKU exists for this vendor's hardware.
      supportsDualCpuFlash: false,
      supportsE2E: true,
      // Rack units per item, read by the Rack Units metric and the rack picture (CONF-230)
      rackHeights: { director: 1, afa: 1, hybrid: 1, jbod: 4 },
      // Director/Velo Configuration Options
      // Director count is entered as a number, not picked from a list: the floor
      // is a rule, the ceiling is whatever the customer buys. (CONF-181)
      directorCountMin: 3,
      expansionDirectorCountMin: 0, // Expansion allows 0
      veloSsdCapacities: [1.92, 3.84, 7.68],
      // Storage Configuration Options
      hddCapacities: [16, 24],
      hybridSsdCapacities: [3.84, 7.68, 15.36],
      afaSsdCapacities: [3.84, 7.68, 15.36],
      productMappings: {
        afaSsd: [
          { capacity: 7.68, partNumber: "VCH-NVME-7.6s", displayName: "7.68TB NVMe" },
          { capacity: 15.36, partNumber: "VCH-NVME-15.3s", displayName: "15.36TB NVMe" }
        ],
        hybridSsd: [
          { capacity: 3.84, partNumber: "VCH-NVME-3.8s", displayName: "3.84TB NVMe" },
          { capacity: 7.68, partNumber: "VCH-NVME-7.6s", displayName: "7.68TB NVMe" },
          { capacity: 15.36, partNumber: "VCH-NVME-15.3s", displayName: "15.36TB NVMe" }
        ],
        hybridHdd: {
          "60": [
            { capacity: 16, partNumber: "VCH-5000-J60-960", displayName: "16TB HDD (960TB total)" },
            { capacity: 24, partNumber: "VCH-5000-J60-1440", displayName: "24TB HDD (1.44PB total)" }
          ],
          "120": [
            { capacity: 16, partNumber: "VCH-5000-J120-1920", displayName: "16TB HDD (1.92PB total)" },
            { capacity: 24, partNumber: "VCH-5000-J120-2880", displayName: "24TB HDD (2.88PB total)" }
          ]
        }
      },

      // Data processing options
      compressionRatios: [1.0, 1.25, 1.5, 1.75, 2.0, 3.0, 4.0, 5.0],

      // Network Options
      // CONF-93. Four options, mapping 1:1 onto the four CCI bundle families
      // (see cciSkuMapping.json). Ports-per-NIC come from the CONF-93
      // acceptance criteria; directorNicCards follows the CCI director
      // descriptions — single-port options are 2x SP and take OCP+PCIe.
      // Throughput ceilings deliberately live in metricsDb, not here: this is
      // catalog data, that is performance data.
      networkOptions: [
        // The next three options are ONE physical card, the Broadcom 200/100GbE
        // dual-port QSFP112 pair, in three link modes (Petros/Chris 2026-09-16,
        // Ken Haugen on CONF-93 2026-07-22). At native 400Gb the card aggregates
        // 2x200Gb and one port is disabled, so 400GbE carries portsPerNic 1 and
        // needs OCP+PCIe to reach two usable ports on a director; 200GbE and
        // 100GbE run the card dual-port off a single OCP card. They differ only
        // in link speed and therefore in cardCeiling, not in hardware.
        {
          id: '400GbE',
          label: '400GbE',
          vendor: 'Broadcom',
          cciSlot: 2,
          portsPerNic: 1,
          directorNicCards: 'OCP+PCIe',
          partNumbers: { ocp: 'VCH-5000-200GBE-2P-OCP', pcie: 'VCH-5000-200GBE-2P-PCIe' },
        },
        {
          id: '200GbE',
          label: '200GbE',
          vendor: 'Broadcom',
          cciSlot: 1,
          // The CCI sheet has no native 200GbE bundle — its four variants stop at
          // slot 1 "DP 100GbE" and slot 2 "2x SP 400GbE". 200GbE is the same card
          // and the same node build as slot 1, so it books the slot-1 bundle and
          // is BOM-identical to 100GbE. User decision 2026-09-17. FLAGGED: the
          // emitted CCI description still reads "DP 100GbE"; Chris/Petros to
          // confirm or issue a real 200GbE row (Ken's open question 2, CONF-93).
          cciVariant: '100GbE',
          portsPerNic: 2,
          directorNicCards: 'OCP',
          partNumbers: { ocp: 'VCH-5000-200GBE-2P-OCP', pcie: 'VCH-5000-200GBE-2P-PCIe' },
        },
        {
          id: 'NDR200/200GbE',
          label: 'NDR200/200GbE',
          vendor: 'NVIDIA',
          cciSlot: 3,
          portsPerNic: 2,
          directorNicCards: 'OCP',
          partNumbers: { ocp: 'VCH-5000-NDR200-2P-OCP', pcie: 'VCH-5000-NDR200-2P-PCIe' },
        },
        {
          id: 'NDR/400GbE',
          label: 'NDR/400GbE',
          vendor: 'NVIDIA',
          cciSlot: 4,
          portsPerNic: 1,
          directorNicCards: 'OCP+PCIe',
          partNumbers: { ocp: 'VCH-NDR400-1P-OCP', pcie: 'VCH-NDR400-1P-PCIE' },
        },
        {
          id: '100GbE',
          label: '100GbE',
          vendor: 'Broadcom',
          cciSlot: 1,
          portsPerNic: 2,
          directorNicCards: 'OCP',
          partNumbers: { ocp: 'VCH-5000-200GBE-2P-OCP', pcie: 'VCH-5000-200GBE-2P-PCIe' },
        },
      ],
      networkProtocols: ['TCP', 'RDMA'],

      // DELL sells no hardware (softwareSubscriptionOnly) — no hardware catalog
      // to categorise. Present-and-empty, so `categories ?? []` never has to
      // guess whether this vendor was simply forgotten.
      catalogCategories: [],

      // DELL sells no hardware — nothing to edit. Present-and-empty, matching
      // its empty catalogCategories.
      catalogEditableCategories: [],

      // DELL sells no hardware — no product families. Present-and-empty,
      // matching its empty catalogCategories.
      productFamilies: [],
    },

    businessRules: {
      forceConsultationMode: false,
      softwareSubscriptionOnly: true,  // Dell hardware is supplied by Dell; quote shows software subscription only
      requiresSpecialPricing: false,
      minSubscriptionMonths: 36,
      defaultAfaCompressionRatio: 1.0,
      defaultHybridCompressionRatio: 1.25,
      registrationMultiplier: 1.4
    },
    
    databases: {
      products: productDbDELL,
      metrics: metricsDbDELL
    },
    
    termsAndConditions: {
      title: 'Terms and Conditions',
      content: `**VDURA COMPLETE SOLUTION TERMS AND CONDITIONS**

**Solution Overview**

**The solution quoted consists of Software-as-a-Service (SaaS) provided directly by VDURA, Inc., and (2) VDURA certified commodity storage hardware provided by a certified partner of VDURA. Customer must issue separate purchase orders for each component: one purchase order to VDURA for SaaS services, and one purchase order to the designated partner for hardware.**

**Purchase orders may be emailed to: orders@vdura.com or faxed to: Sales Operations (001) 510-952-3094**

**VDURA SaaS Terms and Conditions**

1.\t**Contract Formation** VDURA, Inc. ("VDURA") will provide software-as-a-service ("SaaS") offerings and related support (collectively, the "Services") to the Customer (defined as the end user of the Services who is purchasing or subscribing to VDURA's Services and entering into this Agreement) subject exclusively to these Terms and Conditions ("Agreement"). By placing an order for, subscribing to, or using VDURA's Services, Customer agrees to be bound by this Agreement. The Agreement will become effective upon VDURA's acceptance of Customer's order, which may occur through provision of the Services or written confirmation. Any modifications must be explicitly agreed in writing by both parties. A third-party system integrator will deliver and integrate the specified hardware ("Solution Hardware," defined in the Customer's executed quote) with VDURA software prior to delivery. VDURA provides warranty, maintenance, and support services for purchased products according to the Customer's selected service offerings.

Support includes (i) accepting initial service requests via my.VDURA.com, telephone, email, or electronic transmission regarding Solution ; (ii) commercially reasonable efforts to diagnose and resolve issues by trained personnel; (iii) responding to technical, billing, and general inquiries about Solution ; and (iv) distributing Updates. "Update(s)" means subsequent releases of VDURA software products or documentation generally available to supported Customers without additional license fees, excluding separately sold or licensed programs.

2.\t**Pricing and Payment** All software and support (SaaS) pricing is subscription-based, stated in VDURA's official quote or invoice, exclusive of taxes, duties, tariffs, and additional fees (such as handling, processing, or third-party charges), which remain Customer's responsibility. All payment terms for VDURA invoices are strictly net thirty (30) days from invoice date unless otherwise specified. Late payments incur interest at 1.5% per month or the maximum permitted by law.

Customers will be billed separately by the hardware provider for the Solution Hardware and by VDURA for software, support, and installation services. Customers must issue two separate purchase orders (POs) that will be billed against separately for the solution: one PO to be issued to and invoiced by the hardware provider for the hardware, and another PO to be issued to and invoiced by VDURA for software, support, and installation services.

3.\t**Service Delivery** VDURA provides the Services via secure online access. Customer gains immediate access to the my.VDURA.com service portal.

4.\t**Software License** SaaS Licensing and Usage Services are licensed, not sold, strictly according to VDURA's End-User License Agreement (EULA). The Customer is granted a non-exclusive, non-transferable license for the duration of the subscription period. Unauthorized sharing, copying, or modification of the Services is prohibited.

5.\t**Software Updates and Maintenance** VDURA continuously updates the Services, providing automatic and periodic enhancements, bug fixes, and improvements without additional charges during the subscription period. Customer must accept and deploy updates promptly to maintain optimal functionality and support. "Update(s)" refers to subsequent releases of VDURA software products or documentation generally available at no additional fee, provided the Customer is under a current support subscription.

6.\t**Support Services**
\t\t**A. Support Commencement** Support services commence on the equipment ship date plus thirty (30) days, rounded up to the nearest first (1st) or sixteenth (16th) of the month, unless otherwise agreed to in writing by both parties.
\t\t**B. Technical Support** Unless governed by a separate Statement of Work (SOW), VDURA provides technical support using commercially reasonable efforts to identify defective source code, provide corrections, workarounds, and/or patches to correct program errors. VDURA offers support via my.VDURA.com, email, and 24/7 telephone. VDURA logs each service request (trouble ticket), provides technical support request identification numbers, and communicates resolution plans. VDURA uses commercially reasonable efforts to resolve significant program errors by offering workarounds, patches, or corrections in subsequent Updates. Customer acknowledges that technical support may require remote access or a duplicate of Customer's platform or application.
\t\t**C. Email Assistance** VDURA provides reasonable email support for technical inquiries from Customer's designated technical liaison regarding the use and operation of VDURA Products, responding promptly to inquiries.
\t\t**D. Support Exclusions** VDURA is not required to provide support for: (a) unsupported software versions; (b) unpaid fees; (c) breach of this Agreement; (d) unauthorized or improper use; (e) use on unsupported platforms; (f) damage or errors caused by misuse, neglect, unauthorized modifications, defective media, third-party acts, or refusal to implement Updates. Additionally, VDURA is not obligated to address errors from modifications not performed by VDURA or media duplication errors.
\t\t**E. Customer Responsibilities** Customer must designate qualified technical staff and at least one alternate as primary liaisons for technical support communications. Customer may substitute technical contacts with equal or better qualifications upon prior notice. Customer agrees to pay additional fees at VDURA's standard rates for support requests that fall outside VDURA's obligations or are caused by hardware or software not supplied or certified by VDURA. VDURA retains sole discretion in accepting or rejecting enhancement requests.

7.\t**Warranty and Liability** The Services are provided "as is" without implied warranties of merchantability or fitness for a particular purpose. VDURA explicitly disclaims all warranties except those expressly stated herein. VDURA's liability, to the maximum extent permitted by applicable law, shall not exceed fees paid by Customer for Services during the preceding twelve (12) months. VDURA disclaims liability for indirect, incidental, consequential, or special damages.

8.\t**Claims and Refunds** Customer claims relating to Service performance must be reported within thirty (30) calendar days of occurrence. VDURA will investigate and, at its discretion, may offer remedies such as service credits or adjustments. Physical product returns require an authorized Return Material Authorization (RMA) number and adherence to VDURA's RMA policies.

9.\t**Intellectual Property (IP)** Each party retains all rights, titles, and interest in and to its own intellectual property rights. Nothing in this Agreement transfers any ownership of intellectual property rights between VDURA and the Customer. Customer acknowledges that all VDURA-provided software, documentation, and related materials constitute VDURA's intellectual property, and Customer shall not acquire any ownership or proprietary interest therein.

10.\t**Force Majeure** VDURA is not liable for any failure or delay in performing its obligations due to circumstances beyond its control, including but not limited to acts of nature, cyber-attacks, third-party service disruptions, pandemics, or governmental actions.

11.\t**Compliance and Indemnification** Customer must comply with all applicable laws regarding data protection (GDPR, CCPA), cybersecurity, export controls, anti-corruption, and use of the Services. Customer agrees to indemnify and hold VDURA harmless from claims arising from unauthorized use, non-compliance, or misuse of the Services.

12.\t**Data Protection and Privacy** VDURA maintains industry-standard measures to ensure the security and confidentiality of Customer data. VDURA adheres to recognized data protection standards, including but not limited to GDPR and SOC 2 compliance. Customer agrees that VDURA may access Customer data solely for the purposes of providing and improving Services and complying with applicable law.

13.\t**General Provisions** This Agreement is governed by the laws of the State of Delaware, United States, without reference to conflict of laws principles. Any disputes arising under this Agreement will be resolved exclusively in Delaware's federal or state courts. Unenforceable provisions shall not affect the Agreement's remainder. Electronic signatures and electronic acceptance of this Agreement constitute valid execution.

© 2025 VDURA, Inc. All rights reserved.`,
      orderText: ''
    }
  },

  SMC: {
    id: 'SMC',
    displayName: 'Supermicro',
    isActive: true,

    metadata: {
      cognitoGroup: 'smcHardware',
      hardwarePartner: 'SUPER MICRO COMPUTER, INC.'
    },

    branding: {
      logo: supermicroLogo,
      combinedLogo: vduraSmcLogo,
      logoClassName: 'w-[500px]',
      logoStyle: undefined,

      // Theme follows supermicro.com branding: clean white surfaces, deep
      // Supermicro navy (#003A70), link blue (#0067B1), with the brand's
      // green (#00953B) and red (#D7282F) as accent colors.
      theme: {
        // Header & Cards — white header bar with navy title (supermicro.com style)
        cardHeader: 'bg-white border-b-4 border-[#00953B]',
        cardTitle: 'text-xl font-bold text-[#003A70]',

        // Buttons — Supermicro link blue, red reserved for quote CTA
        button: 'bg-[#0067B1] hover:bg-[#00548F] text-white',
        saveButton: 'bg-[#0067B1] hover:bg-[#00548F] text-white',
        loadButton: 'bg-[#00953B] hover:bg-[#007A30] text-white',
        summaryButton: 'bg-[#0067B1] hover:bg-[#00548F] text-white',
        quoteButton: 'bg-[#D7282F] hover:bg-[#B22025] text-white',

        // Accents
        accentColor: 'bg-[#0067B1]',
        accentHover: 'hover:bg-[#3387C1]',

        // Form Elements — white dropdowns with dark text (supermicro.com style)
        selectTrigger: 'hover:bg-gray-100/50',
        selectItem: 'text-[#0f172a] hover:bg-gray-200',
        inputField: 'border border-gray-300 hover:bg-gray-100/50 bg-white text-[#0f172a]',

        // Toggle — Supermicro green ("We Keep IT Green")
        toggleActive: 'bg-[#00953B]',
        toggleInactive: 'bg-gray-700',
        toggleDot: 'bg-white',
        toggleLabelClassName: 'text-[#003A70]',

        // Table — Supermicro navy (force white header cells; .table-head defaults to dark red)
        tableHeader: 'bg-[#003A70] text-white font-bold [&_th]:text-white',
        tableRow: 'hover:bg-gray-100/40'
      }
    },

    capabilities: {
      // Software licenses are declared per option: `licensing.termBased: true`
      // selects the per-TB term model (§5.2 of the SMC vendor port design);
      // options without `licensing` fall through to the classic 10TB/month
      // default. `skus` values keyed by duration month are resolved via
      // `capabilities.subscriptionDurations`; string values are static.
      serviceOptions: [
        {
          key: 'smcSoftwareOnly',
          label: 'Software Only',
          licensing: { unitTB: 1, termBased: true },
          skus: {
            ssd: {
              12: 'VDP-VDURACare-1-SMC-HP-SW-1YR',
              36: 'VDP-VDURACare-1-SMC-HP-SW-3YR',
              60: 'VDP-VDURACare-1-SMC-HP-SW-5YR'
            },
            hdd: {
              12: 'VDP-VDURACare-1-SMC-C-SW-1YR',
              36: 'VDP-VDURACare-1-SMC-C-SW-3YR',
              60: 'VDP-VDURACare-1-SMC-C-SW-5YR'
            },
            ssd_license: null,
            ssd_support: null,
            hdd_license: null,
            hdd_support: null
          }
        },
        {
          key: 'smcNbdMedia',
          hidden: true,  // not offered in the dropdown; kept for saved quotes
          label: 'Next Business Day Media Only',
          skus: {
            ssd: 'VDP-VDURACare-10-SMC-HP-Media',
            hdd: 'VDP-VDURACare-10-SMC-C-Media',
            ssd_license: null,
            ssd_support: 'HW-Support-HP-NBD',
            hdd_license: null,
            hdd_support: 'HW-Support-C-NBD'
          }
        },
        {
          key: 'smcNoReturnMedia',
          hidden: true,  // not offered in the dropdown; kept for saved quotes
          label: 'No Return Media Only',
          skus: {
            ssd: 'VDP-VDURACare-10-SMC-HP-NR-Media',
            hdd: 'VDP-VDURACare-10-SMC-C-NR-Media',
            ssd_license: null,
            ssd_support: 'HW-Support-HP-NBD-NR-Media',
            hdd_license: null,
            hdd_support: 'HW-Support-C-NBD-NR-Media'
          }
        }
      ],
      defaultJbodSize: 60,
      availableJbodSizes: [60, 90],  // Supermicro 4U60 and 4U90 top-load JBODs
      jbodSizeLabels: {
        60: "4U60 | x60 HDDs",
        90: "4U90 | x90 HDDs"
      },
      availableSpares: [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26],
      supportsHybrid: true,
      supportsAFA: true,
      // CONF-94: MLEC ships on Supermicro, on the 4U90 only. 90 / 12 VPODs is
      // 7.5, which the hardware realises as a mix of 7- and 8-drive VPODs; MLEC
      // still takes exactly one drive per VPOD, so 12 of the 90 go to parity
      // (13.3%) whatever the split. The 4U60 is excluded: 4+1 per VPOD would
      // cost 20% of usable capacity. Enforced by mlecEligible.
      supportsMLEC: true,
      mlecJbodSizes: [90],
      // CONF-210: no Dual CPU flash node SKU exists for this vendor's hardware.
      supportsDualCpuFlash: false,
      supportsE2E: true,
      // Software licenses are sold as 1/3/5-year term SKUs, so only these durations apply
      subscriptionDurations: [12, 36, 60],
      // Rack units per item, read by the Rack Units metric and the rack picture (CONF-230).
      // The Mixed Fleet server is 2U; directors and flash nodes are 1U; both JBODs are 4U.
      rackHeights: { director: 1, afa: 1, hybrid: 2, jbod: 4 },
      // Director/Velo Configuration Options
      // Director count is entered as a number, not picked from a list: the floor
      // is a rule, the ceiling is whatever the customer buys. (CONF-181)
      directorCountMin: 3,
      expansionDirectorCountMin: 0, // Expansion allows 0
      veloSsdCapacities: [1.92, 3.84, 7.68],
      // Storage Configuration Options
      hddCapacities: [12, 16, 24, 30],
      hybridSsdCapacities: [3.84, 7.68, 15.36, 30.72],
      afaSsdCapacities: [3.84, 7.68, 15.36, 30.72],
      productMappings: {
        afaSsd: [
          { capacity: 3.84, partNumber: "VCH-NVME-3.8s", displayName: "3.84TB NVMe" },
          { capacity: 7.68, partNumber: "VCH-NVME-7.6s", displayName: "7.68TB NVMe" },
          { capacity: 15.36, partNumber: "VCH-NVME-15.3s", displayName: "15.36TB NVMe" },
          { capacity: 30.72, partNumber: "VCH-NVME-30.7s", displayName: "30.72TB NVMe" }
        ],
        hybridSsd: [
          { capacity: 3.84, partNumber: "VCH-NVME-3.8s", displayName: "3.84TB NVMe" },
          { capacity: 7.68, partNumber: "VCH-NVME-7.6s", displayName: "7.68TB NVMe" },
          { capacity: 15.36, partNumber: "VCH-NVME-15.3s", displayName: "15.36TB NVMe" },
          { capacity: 30.72, partNumber: "VCH-NVME-30.7s", displayName: "30.72TB NVMe" }
        ],
        hybridHdd: {
          60: [
            { capacity: 12, partNumber: "VCH-5000-J60-720", displayName: "12TB HDD (720TB total)" },
            { capacity: 16, partNumber: "VCH-5000-J60-960", displayName: "16TB HDD (960TB total)" },
            { capacity: 24, partNumber: "VCH-5000-J60-1440", displayName: "24TB HDD (1.44PB total)" },
            { capacity: 30, partNumber: "VCH-5000-J60-1800", displayName: "30TB HDD (1.8PB total)" }
          ],
          90: [
            { capacity: 12, partNumber: "VCH-5000-J90-1080", displayName: "12TB HDD (1.08PB total)" },
            { capacity: 16, partNumber: "VCH-5000-J90-1440", displayName: "16TB HDD (1.44PB total)" },
            { capacity: 24, partNumber: "VCH-5000-J90-2160", displayName: "24TB HDD (2.16PB total)" },
            { capacity: 30, partNumber: "VCH-5000-J90-2700", displayName: "30TB HDD (2.7PB total)" }
          ]
        }
      },

      // Data processing options
      compressionRatios: [1.0, 1.25, 1.5, 1.75, 2.0, 3.0, 4.0, 5.0],

      // Network Options
      // CONF-93. Four options, mapping 1:1 onto the four CCI bundle families
      // (see cciSkuMapping.json). Ports-per-NIC come from the CONF-93
      // acceptance criteria; directorNicCards follows the CCI director
      // descriptions — single-port options are 2x SP and take OCP+PCIe.
      // Throughput ceilings deliberately live in metricsDb, not here: this is
      // catalog data, that is performance data.
      networkOptions: [
        // The next three options are ONE physical card, the Broadcom 200/100GbE
        // dual-port QSFP112 pair, in three link modes (Petros/Chris 2026-09-16,
        // Ken Haugen on CONF-93 2026-07-22). At native 400Gb the card aggregates
        // 2x200Gb and one port is disabled, so 400GbE carries portsPerNic 1 and
        // needs OCP+PCIe to reach two usable ports on a director; 200GbE and
        // 100GbE run the card dual-port off a single OCP card. They differ only
        // in link speed and therefore in cardCeiling, not in hardware.
        {
          id: '400GbE',
          label: '400GbE',
          vendor: 'Broadcom',
          cciSlot: 2,
          portsPerNic: 1,
          directorNicCards: 'OCP+PCIe',
          partNumbers: { ocp: 'VCH-5000-200GBE-2P-OCP', pcie: 'VCH-5000-200GBE-2P-PCIe' },
        },
        {
          id: '200GbE',
          label: '200GbE',
          vendor: 'Broadcom',
          cciSlot: 1,
          // The CCI sheet has no native 200GbE bundle — its four variants stop at
          // slot 1 "DP 100GbE" and slot 2 "2x SP 400GbE". 200GbE is the same card
          // and the same node build as slot 1, so it books the slot-1 bundle and
          // is BOM-identical to 100GbE. User decision 2026-09-17. FLAGGED: the
          // emitted CCI description still reads "DP 100GbE"; Chris/Petros to
          // confirm or issue a real 200GbE row (Ken's open question 2, CONF-93).
          cciVariant: '100GbE',
          portsPerNic: 2,
          directorNicCards: 'OCP',
          partNumbers: { ocp: 'VCH-5000-200GBE-2P-OCP', pcie: 'VCH-5000-200GBE-2P-PCIe' },
        },
        {
          id: 'NDR200/200GbE',
          label: 'NDR200/200GbE',
          vendor: 'NVIDIA',
          cciSlot: 3,
          portsPerNic: 2,
          directorNicCards: 'OCP',
          partNumbers: { ocp: 'VCH-5000-NDR200-2P-OCP', pcie: 'VCH-5000-NDR200-2P-PCIe' },
        },
        {
          id: 'NDR/400GbE',
          label: 'NDR/400GbE',
          vendor: 'NVIDIA',
          cciSlot: 4,
          portsPerNic: 1,
          directorNicCards: 'OCP+PCIe',
          partNumbers: { ocp: 'VCH-NDR400-1P-OCP', pcie: 'VCH-NDR400-1P-PCIE' },
        },
        {
          id: '100GbE',
          label: '100GbE',
          vendor: 'Broadcom',
          cciSlot: 1,
          portsPerNic: 2,
          directorNicCards: 'OCP',
          partNumbers: { ocp: 'VCH-5000-200GBE-2P-OCP', pcie: 'VCH-5000-200GBE-2P-PCIe' },
        },
      ],
      networkProtocols: ['TCP', 'RDMA'],

      // D1: productDb-SMC.json only — no catalog rows, no SMC PriceEdition yet.
      // Present-and-empty, matching DELL's pattern, until catalog authoring
      // for SMC is a follow-up (present-even-if-empty: ProductsView
      // dereferences productFamilies without ?? []).
      catalogCategories: [],
      catalogEditableCategories: [],
      productFamilies: [],
    },

    businessRules: {
      forceConsultationMode: false,
      softwareSubscriptionOnly: true,  // Supermicro hardware is supplied by Supermicro; quote shows software subscription only
      requiresSpecialPricing: false,
      minSubscriptionMonths: 36,
      defaultAfaCompressionRatio: 1.0,
      defaultHybridCompressionRatio: 1.25,
      registrationMultiplier: 1.4
    },

    databases: {
      products: productDbSMC,
      metrics: metricsDbSMC
    },

    termsAndConditions: {
      title: 'VDURA-Supermicro Partnership Terms and Conditions',
      content: `VDURA-Supermicro Partnership Terms and Conditions
1.\tContract Formation This agreement governs the provision of VDURA software-as-a-service ("SaaS") offerings integrated with Supermicro hardware solutions. VDURA, Inc. ("VDURA") and Super Micro Computer, Inc. ("Supermicro") will collaborate to deliver comprehensive storage solutions to the Customer. Contracts are formed upon written acceptance by both VDURA and Supermicro of Customer's orders.
2.\tPricing and Payment Supermicro hardware and VDURA software/support are priced separately. Supermicro will invoice hardware components directly to Customer. VDURA will invoice software, support, and professional services separately. Payment terms are net thirty (30) days from respective invoice dates. Customers must issue separate purchase orders for hardware (to Supermicro) and software/services (to VDURA).
3.\tService Delivery Supermicro provides hardware delivery, installation, and initial configuration. VDURA provides software licensing, ongoing support, and system optimization services. Delivery schedules are coordinated between Supermicro and VDURA to ensure seamless integration.
4.\tSoftware License and Hardware Warranty VDURA software is licensed according to VDURA's End-User License Agreement. Supermicro hardware is covered under Supermicro's standard warranty terms. Integrated system support requires coordination between both parties' support organizations.
5.\tSupport Services VDURA provides software support including 24/7 technical assistance and system monitoring. Supermicro provides hardware support including replacement parts and on-site service. Escalation procedures ensure seamless customer experience across both software and hardware components.
6.\tPartnership Obligations Both VDURA and Supermicro commit to maintaining technical competencies, providing timely support, and ensuring solution compatibility. Regular technical reviews and joint customer meetings may be required to maintain service quality.
7.\tIntellectual Property Rights Each party retains ownership of respective intellectual property. Supermicro retains rights to hardware designs and firmware. VDURA retains rights to software and related documentation. No intellectual property transfers occur through this partnership arrangement.
8.\tLiability and Warranty Each party limits liability to direct damages not exceeding fees paid for respective components during the preceding twelve months. Consequential, indirect, and special damages are excluded. Hardware warranties are provided by Supermicro; software warranties by VDURA.
9.\tCompliance and Export Controls Both parties must comply with applicable export control regulations, data protection laws, and cybersecurity requirements. Customer agrees to comply with all applicable laws regarding use of the integrated solution.
10.\tGeneral Provisions This agreement is governed by applicable jurisdiction laws. Disputes will be resolved through appropriate legal channels. Electronic signatures constitute valid execution.
© 2026 VDURA, Inc. and Super Micro Computer, Inc. All rights reserved.`,
      orderText: 'Hardware orders: Contact your VDURA representative. Software orders: orders@vdura.com or fax (001) 510-952-3094'
    }
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get vendor configuration by ID
 * @param {string} vendorId - Vendor identifier (e.g., 'AIC', 'DELL')
 * @returns {VendorConfig} Vendor configuration object
 */
export const getVendor = (vendorId) => {
  const vendor = VENDORS[vendorId?.toUpperCase()];
  if (!vendor) {
    console.warn(`Unknown vendor: ${vendorId}, falling back to AIC`);
    return VENDORS.AIC;
  }
  return vendor;
};

/**
 * Get all active vendors
 * @returns {VendorConfig[]} Array of active vendor configurations
 */
export const getActiveVendors = () => {
  return Object.values(VENDORS).filter(v => v.isActive);
};

/**
 * Cognito groups that make someone a full-configurator user. `specialPricing`
 * is included because it already implies fullConfigurator everywhere else in
 * the app (see useUserPermissions), and the two notions must not drift.
 */
const FULL_CONFIGURATOR_GROUPS = ['fullConfigurator', 'specialPricing'];

/**
 * Check whether the user is a full-configurator user. Single source of truth —
 * useUserPermissions imports this rather than open-coding the group list.
 * @param {string[]} userGroups - Array of Cognito group names
 * @returns {boolean} True if the user has full configurator access
 */
export const hasFullConfiguratorAccess = (userGroups = []) =>
  FULL_CONFIGURATOR_GROUPS.some(g => userGroups.includes(g));

/**
 * Get vendor IDs that user has access to based on Cognito groups. Registry
 * order (AIC, DELL, SMC) is preserved: the FIRST entry is the vendor a user
 * lands on, so ConfiguratorGate and useVendor both rely on this ordering.
 * @param {string[]} userGroups - Array of Cognito group names
 * @returns {string[]} Array of accessible vendor IDs; empty when the user holds
 *   no vendor group (they see the Configurator Home page, not a configurator)
 */
export const getAccessibleVendors = (userGroups = []) => {
  // Delegates the per-vendor decision to hasVendorAccess so the two rules
  // cannot drift; the isActive filter stays here.
  return Object.values(VENDORS)
    .filter(vendor => vendor.isActive && hasVendorAccess(vendor.id, userGroups))
    .map(v => v.id);
};

/**
 * The Cognito groups that grant hardware access, one per active vendor. The
 * V2 ability model (shared/access/defineAbility) and the admin roster read
 * this rather than hand-listing the groups a third time.
 * @returns {string[]}
 */
export const getVendorAccessGroups = () =>
  getActiveVendors().map(vendor => vendor.metadata.cognitoGroup);

/**
 * Check if user has access to a specific vendor.
 *
 * Vendor access is decided ONLY by the vendor's own Cognito group
 * (aicHardware / dellHardware / smcHardware). fullConfigurator and
 * specialPricing decide how much of the configurator a user sees — the
 * consultation toggle, BOM, pricing, quotes — never which hardware; see
 * hasFullConfiguratorAccess. The two axes are independent on purpose, so a
 * rep can be given exactly one vendor at any tier.
 *
 * Unknown vendor ids are false. This deliberately does NOT go through
 * getVendor(), whose unknown-id fallback to AIC would turn a typo into a grant.
 *
 * Note: this does not consider `isActive` — useVendor.switchVendor rejects
 * inactive vendors before it gets here, and getAccessibleVendors filters them.
 *
 * @param {string} vendorId - Vendor identifier
 * @param {string[]} userGroups - Array of Cognito group names
 * @returns {boolean} True if user has access
 */
export const hasVendorAccess = (vendorId, userGroups = []) => {
  const vendor = typeof vendorId === 'string' ? VENDORS[vendorId.toUpperCase()] : undefined;
  if (!vendor) return false;
  return userGroups.includes(vendor.metadata.cognitoGroup);
};

/**
 * Get vendor product database
 * @param {string} vendorId - Vendor identifier
 * @returns {Object} Product database JSON
 */
export const getVendorProductDb = (vendorId) => {
  return getVendor(vendorId).databases.products;
};

/**
 * Get vendor metrics database
 * @param {string} vendorId - Vendor identifier
 * @returns {Object} Metrics database JSON
 */
export const getVendorMetricsDb = (vendorId) => {
  return getVendor(vendorId).databases.metrics;
};

/**
 * Get vendor theme/styling
 * @param {string} vendorId - Vendor identifier
 * @returns {Object} Theme object with CSS classes
 */
export const getVendorTheme = (vendorId) => {
  return getVendor(vendorId).branding.theme;
};

/**
 * Get vendor business rules
 * @param {string} vendorId - Vendor identifier
 * @returns {Object} Business rules object
 */
export const getVendorRules = (vendorId) => {
  return getVendor(vendorId).businessRules;
};

/**
 * Get vendor capabilities
 * @param {string} vendorId - Vendor identifier
 * @returns {Object} Capabilities object
 */
export const getVendorCapabilities = (vendorId) => {
  return getVendor(vendorId).capabilities;
};

const RACK_HEIGHT_KEYS = ['director', 'afa', 'hybrid', 'jbod'];

/**
 * Rack units per item for a vendor: { director, afa, hybrid, jbod }. The Rack
 * Units metric and the rack picture both read this, so they cannot disagree.
 * Throws rather than assume 1U: that silent default is how the SMC 2U Mixed
 * Fleet node was counted as 1U (CONF-230).
 * @param {string} vendorId - Vendor identifier
 * @returns {{director: number, afa: number, hybrid: number, jbod: number}}
 */
export const getRackHeights = (vendorId) => {
  const vendor = getVendor(vendorId);
  const heights = vendor.capabilities?.rackHeights;
  const missing = RACK_HEIGHT_KEYS.filter((key) => !(heights?.[key] > 0));
  if (missing.length > 0) {
    throw new Error(`Vendor ${vendor.id} has no rack height for: ${missing.join(', ')}`);
  }
  return heights;
};

/**
 * Get vendor terms and conditions
 * @param {string} vendorId - Vendor identifier
 * @returns {Object} Terms and conditions object
 */
export const getVendorTerms = (vendorId) => {
  return getVendor(vendorId).termsAndConditions;
};

/**
 * Check whether a vendor operates in software-subscription-only mode.
 * In this mode the BOM, PDF, and UI display only the software/subscription
 * line items; hardware is supplied by the vendor partner and not itemised.
 * @param {string} vendorId - Vendor identifier
 * @returns {boolean}
 */
// MORK TODO - investigate adding more business rule getters to allow easier access to vendor rules and capabilities
export const isSoftwareSubscriptionOnly = (vendorId) => {
  return getVendor(vendorId).businessRules.softwareSubscriptionOnly === true;
};

/**
 * Validate vendor configuration (for testing/development)
 * @param {string} vendorId - Vendor identifier
 * @returns {Object} Validation result with errors array
 */
export const validateVendor = (vendorId) => {
  const vendor = VENDORS[vendorId];
  const errors = [];
  
  if (!vendor) {
    errors.push(`Vendor ${vendorId} not found`);
    return { valid: false, errors };
  }
  
  // Check required fields
  const requiredFields = [
    'id', 'displayName', 'isActive',
    'metadata.cognitoGroup', 'metadata.hardwarePartner',
    'branding.logo', 'branding.combinedLogo', 'branding.theme',
    'capabilities.defaultJbodSize', 'capabilities.availableJbodSizes', 'capabilities.rackHeights',
    'businessRules', 'businessRules.forceConsultationMode', 'businessRules.softwareSubscriptionOnly',
    'databases.products', 'databases.metrics',
    'termsAndConditions.title', 'termsAndConditions.content'
  ];
  
  requiredFields.forEach(field => {
    const parts = field.split('.');
    let value = vendor;
    for (const part of parts) {
      value = value?.[part];
    }
    if (value === undefined) {
      errors.push(`Missing required field: ${field}`);
    }
  });
  
  // Validate JBOD sizes
  if (vendor.capabilities?.availableJbodSizes) {
    if (!vendor.capabilities.availableJbodSizes.includes(vendor.capabilities.defaultJbodSize)) {
      errors.push('defaultJbodSize must be in availableJbodSizes array');
    }
  }

  // Term-based service options must declare a SKU for every offered duration —
  // otherwise "added a 24-month term, forgot the 2YR SKUs" quotes a fallback price.
  const durations = vendor.capabilities?.subscriptionDurations || [];
  (vendor.capabilities?.serviceOptions || []).forEach(option => {
    if (!option.licensing?.termBased) return;
    ['ssd', 'hdd'].forEach(tier => {
      const map = option.skus?.[tier];
      durations.forEach(months => {
        if (!map || typeof map !== 'object' || map[months] === undefined) {
          errors.push(`serviceOption ${option.key}: ${tier} skus missing duration ${months}`);
        }
      });
    });
  });

  return {
    valid: errors.length === 0,
    errors,
    warnings: []
  };
};

/**
 * Validate all vendors in registry
 * @returns {Object} Validation results for all vendors
 */
export const validateAllVendors = () => {
  const results = {};
  Object.keys(VENDORS).forEach(vendorId => {
    results[vendorId] = validateVendor(vendorId);
  });
  return results;
};

/**
 * Placeholder vendor id, used by useVendor as its state before Cognito groups
 * have resolved and as a safe object to dereference when a user has NO
 * accessible vendor. It is not an access grant: since 2026-09-22 nobody sees
 * AIC without the aicHardware group, and ConfiguratorGate keeps zero-vendor
 * users out of both shells entirely.
 * @returns {string} Placeholder vendor ID
 */
export const getDefaultVendor = () => 'AIC';

/**
 * Get list of all vendor IDs
 * @returns {string[]} Array of vendor IDs
 */
export const getAllVendorIds = () => {
  return Object.keys(VENDORS);
};

// ============================================================================
// EXPORTS
// ============================================================================

export { VENDORS as VENDOR_REGISTRY };
export default VENDORS;